import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher, { LANG_META, LANG_ORDER } from "@/components/LanguageSwitcher";
import { faqContent } from "@/i18n/faqContent";
import { mediaUrl } from "@/lib/mediaUrl";
import { MessageCircle } from "lucide-react";
import { contactSection } from "@/i18n/contactSection";
import { getWhatsAppUrl } from "@/lib/contact";

const Header = () => {
  const { t, lang, setLang } = useLanguage();
  const reduce = useReducedMotion();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 18);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const links = [
    { href: "#build", label: t.nav.build, id: "nav-build-link" },
    { href: "#work", label: t.nav.work, id: "nav-work-link" },
    { href: "#saas-explainers", label: t.nav.explainers, id: "nav-explainers-link" },
    { href: "#resources", label: t.nav.resources, id: "nav-resources-link" },
    { href: "#faq", label: faqContent[lang].eyebrow, id: "nav-faq-link" },
  ];

  const handleMobileNavigate = (event, href) => {
    event.preventDefault();
    setOpen(false);
    window.setTimeout(() => {
      document.querySelector(href)?.scrollIntoView({
        behavior: reduce ? "auto" : "smooth",
        block: "start",
      });
      window.history.replaceState(null, "", href);
    }, reduce ? 0 : 420);
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: reduce ? 0 : 0.2 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-40 bg-charcoal/10 backdrop-blur-[2px] lg:hidden"
          />
        )}
      </AnimatePresence>

      <header data-testid="site-header" className="pointer-events-none fixed inset-x-0 top-0 z-50 px-3 pt-3 md:px-6 md:pt-5">
        <motion.div
          layout={!reduce}
          className={`pointer-events-auto mx-auto border bg-softwhite/88 backdrop-blur-xl transition-[border-color,box-shadow,background-color] duration-300 ${
            open ? "max-w-xl rounded-[1.75rem] border-line shadow-soft" : "max-w-6xl rounded-full"
          } ${scrolled ? "border-line shadow-[0_18px_45px_-25px_rgba(11,11,11,0.38)]" : "border-white/70 shadow-[0_12px_40px_-28px_rgba(11,11,11,0.28)]"}`}
        >
          <div className="flex h-14 items-center justify-between px-3 ps-5 md:h-[60px] md:px-4 md:ps-6">
            <a
              href="#top"
              data-testid="header-logo-link"
              className="flex shrink-0 items-center rounded-md outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-4"
              aria-label="YacoubCreates"
            >
          <img src={mediaUrl("/brand/logo-primary.png")} alt="YacoubCreates" className="h-[22px] w-auto md:h-6" width="374" height="93" />
            </a>

            <nav data-testid="header-nav" className="hidden items-center gap-1 lg:flex" aria-label="Main">
              {links.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  data-testid={link.id}
                  className="rounded-full px-4 py-2 text-sm font-medium text-graphite/70 outline-none transition-colors duration-200 hover:bg-white hover:text-charcoal focus-visible:ring-2 focus-visible:ring-sage"
                >
                  {link.label}
                </a>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <LanguageSwitcher />
              <a
                href={getWhatsAppUrl(contactSection[lang].whatsappMessage)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={contactSection[lang].whatsappCta}
                data-testid="mobile-whatsapp-cta"
                className="nav-cta flex size-10 shrink-0 items-center justify-center rounded-full text-white focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 sm:hidden"
              >
                <MessageCircle className="size-5" aria-hidden="true" />
              </a>
              <a
                href={getWhatsAppUrl(contactSection[lang].whatsappMessage)}
                target="_blank" rel="noopener noreferrer"
                data-testid="header-contact-cta"
                className="nav-cta hidden h-10 cursor-pointer items-center rounded-full px-5 text-sm font-semibold text-softwhite outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 sm:flex"
              >
                {t.nav.cta}
              </a>
              <button
                type="button"
                onClick={() => setOpen((value) => !value)}
                data-testid="mobile-menu-button"
                aria-label={open ? t.nav.close : t.nav.menu}
                aria-expanded={open}
                className="relative flex size-10 cursor-pointer items-center justify-center rounded-full border border-line bg-white text-graphite outline-none transition-colors duration-200 hover:border-sage focus-visible:ring-2 focus-visible:ring-sage lg:hidden"
              >
                <span className="sr-only">{open ? t.nav.close : t.nav.menu}</span>
                <span className="relative block h-4 w-4" aria-hidden="true">
                  <motion.span
                    animate={{ rotate: open ? 45 : 0, y: open ? 3 : 0 }}
                    transition={{ duration: reduce ? 0 : 0.24 }}
                    className="absolute left-0 top-1 block h-px w-4 bg-current"
                  />
                  <motion.span
                    animate={{ rotate: open ? -45 : 0, y: open ? -3 : 0 }}
                    transition={{ duration: reduce ? 0 : 0.24 }}
                    className="absolute bottom-1 left-0 block h-px w-4 bg-current"
                  />
                </span>
              </button>
            </div>
          </div>

          <AnimatePresence initial={false}>
            {open && (
              <motion.nav
                data-testid="mobile-menu"
                initial={reduce ? false : { opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: reduce ? 0 : 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden border-t border-line/80 lg:hidden"
                aria-label="Mobile"
              >
                <div className="flex flex-col px-3 pb-3 pt-2">
                  {links.map((link, index) => (
                    <motion.a
                      key={link.href}
                      href={link.href}
                      data-testid={`mobile-${link.id}`}
                      initial={reduce ? false : { opacity: 0, y: 18 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: reduce ? 0 : 0.04 + index * 0.045, duration: 0.35 }}
                      onClick={(event) => handleMobileNavigate(event, link.href)}
                      className="flex items-center justify-between rounded-2xl px-4 py-3.5 text-base font-semibold text-graphite outline-none transition-colors duration-200 hover:bg-white focus-visible:ring-2 focus-visible:ring-sage"
                    >
                      {link.label}
                      <span className="text-xs tabular-nums text-graphite/30" dir="ltr">0{index + 1}</span>
                    </motion.a>
                  ))}
                  <a
                    href={getWhatsAppUrl(contactSection[lang].whatsappMessage)}
                    target="_blank" rel="noopener noreferrer"
                    data-testid="mobile-contact-cta"
                    onClick={() => setOpen(false)}
                    className="nav-cta mt-2 flex h-12 w-full cursor-pointer items-center justify-center rounded-2xl text-sm font-semibold text-softwhite"
                  >
                    {t.nav.cta}
                  </a>
                  <div className="mt-3 flex items-center gap-2 border-t border-line pt-3" data-testid="mobile-language-switcher">
                    {LANG_ORDER.map((code) => (
                      <button
                        key={code}
                        type="button"
                        onClick={() => {
                          setLang(code);
                          setOpen(false);
                        }}
                        data-testid={`mobile-language-option-${code}`}
                        className={`flex h-10 flex-1 cursor-pointer items-center justify-center gap-2 rounded-full border text-xs font-semibold transition-colors duration-200 ${
                          lang === code ? "border-sage bg-mist/70 text-charcoal" : "border-line bg-white text-graphite/70"
                        }`}
                      >
                        <img src={LANG_META[code].flag} alt="" className="size-4 rounded-full object-cover" />
                        {LANG_META[code].names[lang]}
                      </button>
                    ))}
                  </div>
                </div>
              </motion.nav>
            )}
          </AnimatePresence>
        </motion.div>
      </header>
    </>
  );
};

export default Header;
